/**
 * P2P invite copy for web: fallback and getP2PCopyWeb.
 * When feature flag enable_p2p_invite_copy_config_web is ON, copy comes from
 * Firebase Remote Config key p2p_invite_copy_web (same JSON shape as app).
 * Otherwise uses fallback (existing web copy). FEC-2422
 */

import { RemoteConfigWeb } from 'config/RemoteConfigWeb';
import { Branding } from 'common/env';

const P2P_FALLBACK_EMAIL_SUBJECT =
    '{{Referrer Name}} invites you to join the {{Org Name}} fundraiser.';
const P2P_FALLBACK_EMAIL_BODY = `Hey there,

We're using ${Branding} to make fundraising super easy. Every time you shop, dine, or book travel, you earn $$ to offset your expenses for {{Org Name}}.

\u2022 Hundreds of brands to choose from\u2014earn money [up to 20%] back on what you're already buying
\u2022 Everyday purchases = earnings\u2014fits your routine so you don't have to add more to your plate
\u2022 Earn anytime, anywhere\u2014whether you're waiting to checkout, dining out for dinner, or shopping online

Download the app and start earning now! {{referral link}}

{{Referrer Name}}`;

const P2P_FALLBACK_SMS_BODY = `Hey! We're using ${Branding} to make fundraising super easy. Every time you shop, dine, or book travel, you earn \u{1F4B0} to offset your expenses for {{Org Name}}. Download the app and start earning now: {{referral link}} -- {{Referrer Name}}`;

export interface P2PInviteCopyConfig {
    SMS: { body: string };
    EMAIL: { subject: string; body: string };
}

function parseP2PInviteCopyConfig(raw: string): P2PInviteCopyConfig | null {
    try {
        const parsed = JSON.parse(raw) as P2PInviteCopyConfig;
        if (
            parsed &&
            typeof parsed.SMS?.body === 'string' &&
            typeof parsed.EMAIL?.subject === 'string' &&
            typeof parsed.EMAIL?.body === 'string'
        ) {
            return parsed;
        }
    } catch {
        // use fallback
    }
    return null;
}

/** Matches {{referral link}}, {{Referral link}}, etc. (Remote Config often uses title case.) */
const REFERRAL_LINK_PLACEHOLDER = /\{\{\s*referral\s+link\s*\}\}/gi;

/** Matches {{app and/or web link}} with any casing/spacing around words. */
const APP_OR_WEB_LINK_PLACEHOLDER = /\{\{\s*app\s+and\/or\s+web\s+link\s*\}\}/gi;

export function replaceP2PPlaceholders(
    template: string,
    orgName: string,
    referrerName: string,
    link: string
): string {
    return template
        .replace(/\{\{Org Name\}\}/g, orgName)
        .replace(/\{\{Referrer Name\}\}/g, referrerName)
        .replace(REFERRAL_LINK_PLACEHOLDER, () => link)
        .replace(APP_OR_WEB_LINK_PLACEHOLDER, () => link);
}

export interface P2PCopyWebResult {
    emailSubject: string;
    emailBody: string;
    smsBody: string;
    templateSource: 'firebase' | 'fallback';
}

export function getP2PCopyWeb(
    orgName: string,
    referrerName: string,
    url: string,
    enableP2PInviteCopyConfigWeb: boolean
): P2PCopyWebResult {
    const replace = (template: string) =>
        replaceP2PPlaceholders(template, orgName, referrerName, url);

    const fallbackEmailBody = P2P_FALLBACK_EMAIL_BODY;
    const fallbackSmsBody = P2P_FALLBACK_SMS_BODY;

    if (!enableP2PInviteCopyConfigWeb) {
        return {
            emailSubject: replace(P2P_FALLBACK_EMAIL_SUBJECT),
            emailBody: replace(fallbackEmailBody),
            smsBody: replace(fallbackSmsBody),
            templateSource: 'fallback',
        };
    }

    // Flag is ON: try to use Firebase Remote Config copy.
    // If Firebase returns valid JSON → use it.
    // If Firebase returns empty/invalid JSON, or throws → fall through to fallback below.
    try {
        const raw = RemoteConfigWeb.getRemoteValue('p2p_invite_copy_web').asString();
        const config = raw?.trim() ? parseP2PInviteCopyConfig(raw) : null;
        if (config) {
            return {
                emailSubject: replace(config.EMAIL.subject.trim()),
                emailBody: replace(config.EMAIL.body.trim()),
                smsBody: replace(config.SMS.body.trim()),
                templateSource: 'firebase',
            };
        }
        // config is null (empty or invalid JSON) — fall through to fallback
    } catch {
        // Firebase read or parse threw — fall through to fallback
    }

    // Fallback: flag is on but Firebase config was empty, invalid, or errored
    return {
        emailSubject: replace(P2P_FALLBACK_EMAIL_SUBJECT),
        emailBody: replace(fallbackEmailBody),
        smsBody: replace(fallbackSmsBody),
        templateSource: 'fallback',
    };
}
